import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { 
  ArrowLeft, 
  Brain, 
  BarChart, 
  Lock, 
  Zap, 
  Rocket, 
  Users,
  TrendingUp,
  Shield,
  Clock,
  CheckCircle,
  Globe,
  Target,
  Lightbulb,
  ArrowRight,
  Sparkles,
  FileText,
  Building,
  Cog,
  Workflow
} from "lucide-react";

type FeatureCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
};

export default function About() {
  const [activeTab, setActiveTab] = useState("mission");

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 font-sans transition-colors duration-300">
      {/* Enhanced Background Elements */}
      <div className="absolute top-[-25%] left-[-15%] w-96 h-96 sm:w-[600px] sm:h-[600px] rounded-full blur-3xl opacity-30 bg-gradient-to-br from-indigo-200/60 to-blue-200/60 dark:from-indigo-900/60 dark:to-blue-900/60 animate-pulse" />
      <div className="absolute bottom-[-30%] right-[-20%] w-96 h-96 sm:w-[700px] sm:h-[700px] rounded-full blur-3xl opacity-30 bg-gradient-to-br from-purple-200/60 to-pink-200/60 dark:from-purple-900/60 dark:to-pink-900/60 animate-pulse" />
      <div className="absolute top-1/3 left-1/4 w-32 h-32 sm:w-64 sm:h-64 rounded-full blur-2xl opacity-20 bg-gradient-to-br from-cyan-200/50 to-teal-200/50 dark:from-cyan-900/50 dark:to-teal-900/50" />
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-blue-400/20 rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="container relative z-10 mx-auto px-4 py-6 sm:py-12 max-w-7xl">
        {/* Enhanced Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.history.back()}
              className="group self-start p-2 rounded-full 
                         bg-white/60 dark:bg-slate-800/60 
                         border border-slate-300/50 dark:border-slate-700/50 
                         text-slate-700 dark:text-slate-300 
                         hover:bg-slate-100/70 dark:hover:bg-slate-700/70 
                         hover:text-slate-900 dark:hover:text-white 
                         hover:border-blue-400/50 
                         transition-all duration-300 
                         hover:shadow-lg hover:shadow-blue-500/20 
                         backdrop-blur-md"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            </Button>

            <div className="flex items-center gap-4">
              <div className="relative p-4 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 dark:from-blue-500/30 dark:to-indigo-500/30 rounded-2xl border border-blue-400/20 backdrop-blur-sm">
                <Rocket className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 dark:bg-green-400 rounded-full animate-ping" />
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 dark:bg-green-400 rounded-full" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent leading-tight">
                  About Us
                </h1>
                <p className="text-slate-600 dark:text-slate-400 text-lg sm:text-xl lg:text-2xl mt-2 flex items-center gap-2">
                  <Sparkles className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  Transforming Credit Assessment with AI
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 p-2 bg-white/30 dark:bg-slate-800/30 rounded-2xl backdrop-blur-sm border border-slate-300/30 dark:border-slate-700/30">
          {[
            { id: "mission", label: "Our Mission", icon: <Target className="h-4 w-4" /> },
            { id: "features", label: "Platform Features", icon: <Zap className="h-4 w-4" /> },
            { id: "process", label: "How It Works", icon: <Workflow className="h-4 w-4" /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Mission Section */}
          {activeTab === "mission" && (
            <Card className="rounded-2xl bg-white/40 dark:bg-slate-800/40 border-slate-300/50 dark:border-slate-700/50 backdrop-blur-xl shadow-2xl transition-all duration-500">
              <CardHeader className="pb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <Target className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
                      Our Mission
                    </CardTitle>
                    <CardDescription className="text-slate-600 dark:text-slate-400 text-xl mt-2">
                      Empowering financial institutions with intelligent automation
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-10">
                {/* Main Mission Statement */}
                <div className="relative p-8 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-300 dark:border-blue-700/30 backdrop-blur-sm">
                  <div className="absolute top-4 right-4">
                    <Lightbulb className="h-8 w-8 text-yellow-500/60 dark:text-yellow-400/60" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                    <Building className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    Transforming Credit Assessment
                  </h3>
                  <p className="text-slate-700 dark:text-slate-300 text-lg leading-relaxed mb-6">
                    We are a technology-driven company committed to transforming the credit assessment process 
                    for banks and financial institutions. Leveraging advanced Artificial Intelligence (AI) and 
                    data analytics, our platform automates and streamlines the preparation of credit proposals, 
                    helping financial institutions enhance efficiency, accuracy, and compliance.
                  </p>
                </div>

                {/* Key Focus Areas */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {missionFocus.map((focus, index) => (
                    <div key={index} className="group relative overflow-hidden rounded-xl p-6 bg-white/50 dark:bg-slate-800/30 border border-slate-300/30 dark:border-slate-700/30 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/70 dark:hover:bg-slate-800/50">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-300">
                          {focus.icon}
                        </div>
                        <h4 className="font-bold text-slate-900 dark:text-white text-lg group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                          {focus.title}
                        </h4>
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{focus.description}</p>
                    </div>
                  ))}
                </div>

                {/* Partnership Section */}
                <div className="p-8 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-300 dark:border-green-700/30">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                    <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
                    Strategic Partnerships
                  </h3>
                  <p className="text-slate-700 dark:text-slate-300 text-lg leading-relaxed">
                    We partner with financial institutions to support their digital transformation goals — 
                    empowering them with smarter tools for faster decision-making, improved risk management, 
                    and enhanced customer service.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Features Section */}
          {activeTab === "features" && (
            <Card className="rounded-2xl bg-white/40 dark:bg-slate-800/40 border-slate-300/50 dark:border-slate-700/50 backdrop-blur-xl shadow-2xl transition-all duration-500">
              <CardHeader className="pb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-green-500/20 rounded-xl">
                    <Zap className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <CardTitle className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
                      Platform Features
                    </CardTitle>
                    <CardDescription className="text-slate-600 dark:text-slate-400 text-xl mt-2">
                      Advanced capabilities designed for modern financial institutions
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {platformFeatures.map((feature, index) => (
                    <FeatureCard key={index} {...feature} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Process Section */}
          {activeTab === "process" && (
            <Card className="rounded-2xl bg-white/40 dark:bg-slate-800/40 border-slate-300/50 dark:border-slate-700/50 backdrop-blur-xl shadow-2xl transition-all duration-500">
              <CardHeader className="pb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-purple-500/20 rounded-xl">
                    <Workflow className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <CardTitle className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
                      How It Works
                    </CardTitle>
                    <CardDescription className="text-slate-600 dark:text-slate-400 text-xl mt-2">
                      Streamlined process for efficient credit assessment
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {/* Process Steps */}
                  <div className="space-y-8">
                    {workflowSteps.map((step, i) => (
                      <div key={i} className="group relative">
                        <div className="flex gap-8 items-start p-8 rounded-2xl bg-white/50 dark:bg-slate-800/30 border border-slate-300/30 dark:border-slate-700/30 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/70 dark:hover:bg-slate-800/50">
                          <div className="flex-shrink-0 w-16 h-16 text-white rounded-full flex items-center justify-center font-bold text-xl bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 group-hover:scale-110 transition-all duration-300">
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 text-blue-600 dark:text-blue-400">
                                {step.icon}
                              </div>
                              <h4 className="font-bold text-2xl text-slate-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                                {step.title}
                              </h4>
                            </div>
                            <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed mb-4">{step.description}</p>
                            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 font-medium">
                              <Clock className="h-4 w-4" />
                              {step.duration}
                            </div>
                          </div>
                          <ArrowRight className="h-6 w-6 text-slate-400 dark:text-slate-600 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-2 transition-all duration-300" />
                        </div>
                        {i < workflowSteps.length - 1 && (
                          <div className="w-0.5 h-8 bg-gradient-to-b from-slate-400 to-transparent dark:from-slate-600 dark:to-transparent ml-16 mt-4" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-block p-8 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40 rounded-2xl border border-blue-300 dark:border-blue-700/30 backdrop-blur-sm">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Ready to Transform Your Credit Assessment?
            </h2>
            <p className="text-slate-700 dark:text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              Join forward-thinking financial institutions using our AI platform to make faster, smarter credit decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                className="group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 px-10 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:shadow-blue-500/40"
                onClick={() => window.history.back()}
              >
                Get Started Today
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, benefits }: FeatureCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-2xl p-8 bg-white/50 dark:bg-slate-800/30 border border-slate-300/30 dark:border-slate-700/30 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/70 dark:hover:bg-slate-800/50">
      <div className="flex items-center gap-4 mb-6">
        <div className="p-4 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <h4 className="font-bold text-2xl text-slate-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
          {title}
        </h4>
      </div>
      <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed mb-6">{description}</p>
      <div className="space-y-2">
        {benefits.map((benefit, index) => (
          <div key={index} className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
            <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />
            <span className="text-sm">{benefit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Data
const missionFocus = [
  {
    icon: <Brain className="h-6 w-6" />,
    title: "AI-Powered Analysis",
    description: "Intelligent analysis of financial statements, borrower data, and market information to generate structured credit proposals."
  },
  {
    icon: <Zap className="h-6 w-6" />,
    title: "Enhanced Efficiency",
    description: "Reduce manual documentation time, allowing teams to focus on strategic evaluation and decision-making."
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "Regulatory Compliance",
    description: "Built with strong understanding of banking processes and regulatory frameworks to ensure compliance."
  },
  {
    icon: <Target className="h-6 w-6" />,
    title: "Data Integrity",
    description: "Ensure consistency, transparency, and data integrity throughout the credit assessment journey."
  },
  {
    icon: <TrendingUp className="h-6 w-6" />,
    title: "Risk Management",
    description: "Improved risk assessment capabilities through comprehensive data analysis and pattern recognition."
  },
  {
    icon: <Globe className="h-6 w-6" />,
    title: "Digital Transformation",
    description: "Support financial institutions in their digital transformation journey with cutting-edge technology."
  }
];

const platformFeatures = [
  {
    icon: <Brain className="w-8 h-8" />,
    title: "Intelligent Document Analysis",
    description: "Advanced AI algorithms that automatically analyze financial statements, borrower data, and market information with precision.",
    benefits: [
      "Automated financial statement processing",
      "Intelligent data extraction and validation",
      "Market trend analysis integration",
      "Real-time data processing capabilities"
    ]
  },
  {
    icon: <FileText className="w-8 h-8" />,
    title: "Structured Credit Proposals",
    description: "Generate comprehensive, insight-based credit proposals in a fraction of traditional processing time.",
    benefits: [
      "Standardized proposal templates",
      "Automated risk assessment integration",
      "Customizable reporting formats",
      "Regulatory compliance built-in"
    ]
  },
  {
    icon: <BarChart className="w-8 h-8" />,
    title: "Advanced Analytics",
    description: "Leverage data analytics to provide deep insights and predictive analysis for better credit decisions.",
    benefits: [
      "Predictive risk modeling",
      "Historical performance analysis",
      "Scenario analysis capabilities",
      "Portfolio optimization insights"
    ]
  },
  {
    icon: <Lock className="w-8 h-8" />,
    title: "Secure & Compliant",
    description: "Enterprise-grade security with built-in compliance frameworks for financial institutions.",
    benefits: [
      "Bank-level security protocols",
      "Regulatory framework compliance",
      "Audit trail and documentation",
      "Data encryption at rest and in transit"
    ]
  },
  {
    icon: <Cog className="w-8 h-8" />,
    title: "Workflow Automation",
    description: "Streamline credit assessment workflows with intelligent automation and process optimization.",
    benefits: [
      "Automated approval workflows",
      "Task assignment and tracking",
      "Collaboration tools for teams",
      "Integration with existing systems"
    ]
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Team Empowerment",
    description: "Enable relationship managers and credit analysts to focus on strategic evaluation rather than manual tasks.",
    benefits: [
      "Role-based access control",
      "Collaborative review features",
      "Decision support tools",
      "Training and onboarding support"
    ]
  }
];

const workflowSteps = [
  {
    icon: <FileText className="h-6 w-6" />,
    title: "Data Collection & Upload",
    description: "Securely upload financial statements, borrower information, and relevant market data through our intuitive platform interface.",
    duration: "2-5 minutes"
  },
  {
    icon: <Brain className="h-6 w-6" />,
    title: "AI Analysis & Processing",
    description: "Our advanced AI algorithms analyze financial data, assess risk factors, and evaluate creditworthiness using machine learning models.",
    duration: "1-3 minutes"
  },
  {
    icon: <BarChart className="h-6 w-6" />,
    title: "Credit Proposal Generation",
    description: "Automatically generate structured, insight-based credit proposals with comprehensive risk assessment and recommendations.",
    duration: "30-60 seconds"
  },
  {
    icon: <CheckCircle className="h-6 w-6" />,
    title: "Review & Decision Support",
    description: "Relationship managers and credit analysts review AI-generated proposals with enhanced insights for informed decision-making.",
    duration: "Flexible based on complexity"
  }
];